// components/ApiBalanceDisplay.js
import React, { useState, useEffect } from 'react';
import { Tag, Spin, Tooltip, Button } from 'antd';
import { DollarOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../config';

const ApiBalanceDisplay = ({ apiType }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchBalance = async () => {
    setLoading(true);
    setError(false);
    
    try {
      const endpoint = apiType === 'openai' 
        ? '/check-openai-balance' 
        : '/check-openrouter-balance';
        
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1${endpoint}`,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );

      if (response.data.success) {
        setBalance(response.data);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [apiType]);

  const getBalanceColor = () => {
    if (!balance || balance.balance === undefined) return 'default';
    
    const amount = balance.balance;
    if (amount > 5) return 'success';
    if (amount > 2) return 'warning';
    return 'error';
  };

  const getBalanceStatus = () => {
    if (!balance || balance.balance === undefined) return 'Unknown';
    
    const amount = balance.balance;
    if (amount > 5) return 'Sufficient';
    if (amount > 2) return 'Low';
    return 'Critical';
  };

  if (loading) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 12 }}>
        <Spin size="small" />
        <span style={{ marginLeft: 8 }}>Checking balance...</span>
      </div>
    );
  }

  if (error || !balance) {
    // Special handling for OpenAI browser-only limitation
    if (apiType === 'openai' && balance?.browser_only) {
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 12 }}>
          <Tooltip title="OpenAI billing data can only be accessed through their dashboard">
            <Tag color="default" icon={<DollarOutlined />}>
              <a href="https://platform.openai.com/usage" target="_blank" rel="noopener noreferrer">
                View in OpenAI Dashboard
              </a>
            </Tag>
          </Tooltip>
        </div>
      );
    }
    
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 12 }}>
        <Tag color="default">Balance unavailable</Tag>
        <Button 
          type="link" 
          size="small" 
          icon={<ReloadOutlined />} 
          onClick={fetchBalance}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 12 }}>
      <Tooltip 
        title={
          apiType === 'openai' 
            ? `Limit: $${balance.limit?.toFixed(2) || 'N/A'} | Used: $${balance.used?.toFixed(2) || '0.00'}`
            : `Credits: $${balance.total_credits?.toFixed(2) || 'N/A'} | Used: $${balance.total_usage?.toFixed(2) || '0.00'}`
        }
      >
        <Tag 
          icon={<DollarOutlined />} 
          color={getBalanceColor()}
        >
          Balance: ${balance.balance?.toFixed(2) || '0.00'} ({getBalanceStatus()})
        </Tag>
      </Tooltip>
      <Button 
        type="link" 
        size="small" 
        icon={<ReloadOutlined />} 
        onClick={fetchBalance}
        style={{ marginLeft: 8 }}
      >
        Refresh
      </Button>
    </div>
  );
};

export default ApiBalanceDisplay;